import { db } from '../../db/database';
import { notifyOutboxChanged } from '../../services/sync/syncEngine';
import type { OrderStatus } from '../../db/types';

const allowedTransitions: Record<OrderStatus, OrderStatus[]> = {
  RECEIVED: ['PROCESSING'],
  PROCESSING: ['READY'],
  READY: ['CLAIMED'],
  CLAIMED: [],
  CANCELLED: [],
  VOIDED: [],
};

export function canTransitionOrder(from: OrderStatus, to: OrderStatus): boolean {
  return allowedTransitions[from].includes(to);
}

export async function transitionOrderStatus(input: {
  orderId: string;
  to: OrderStatus;
  actorId?: string;
  deviceId?: string;
}): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', [db.orders, db.auditLogs, db.outbox], async () => {
    const order = await db.orders.get(input.orderId);
    if (!order) throw new Error('Order not found.');
    if (!canTransitionOrder(order.orderStatus, input.to))
      throw new Error(`Invalid order status transition: ${order.orderStatus} → ${input.to}`);
    const actorId = input.actorId ?? 'cashier-local';
    const deviceId = input.deviceId ?? 'device-local';
    await db.orders.update(order.id, { orderStatus: input.to, updatedAt: now });
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'ORDER_STATUS_CHANGED',
      entityType: 'ORDER',
      entityId: order.id,
      actorId,
      deviceId,
      metadata: JSON.stringify({ from: order.orderStatus, to: input.to }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'ORDER_STATUS_CHANGED',
      aggregateType: 'ORDER',
      aggregateId: order.id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify({ orderId: order.id, from: order.orderStatus, to: input.to }),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
}

export async function releaseOrder(input: {
  orderId: string;
  cashierId?: string;
  deviceId?: string;
}): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', [db.orders, db.auditLogs, db.outbox], async () => {
    const order = await db.orders.get(input.orderId);
    if (!order) throw new Error('Order not found.');
    if (order.orderStatus !== 'READY') throw new Error('Only READY orders can be released.');
    const claimedBy = input.cashierId ?? 'cashier-local';
    const deviceId = input.deviceId ?? 'device-local';
    await db.orders.update(order.id, {
      orderStatus: 'CLAIMED',
      claimedAt: now,
      claimedBy,
      deviceId,
      updatedAt: now,
    });
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'ORDER_CLAIMED',
      entityType: 'ORDER',
      entityId: order.id,
      actorId: claimedBy,
      deviceId,
      metadata: JSON.stringify({ orderNumber: order.orderNumber, claimCode: order.claimCode }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'ORDER_CLAIMED',
      aggregateType: 'ORDER',
      aggregateId: order.id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify({
        orderId: order.id,
        orderNumber: order.orderNumber,
        claimCode: order.claimCode,
        claimedAt: now,
        claimedBy,
        deviceId,
      }),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
}

export async function voidOrder(input: {
  orderId: string;
  reason: string;
  actorId?: string;
  deviceId?: string;
}): Promise<void> {
  const reason = input.reason.trim();
  if (!reason) throw new Error('Void reason is required.');
  const now = new Date().toISOString();
  await db.transaction('rw', [db.orders, db.auditLogs, db.outbox], async () => {
    const order = await db.orders.get(input.orderId);
    if (!order) throw new Error('Order not found.');
    if (order.orderStatus === 'VOIDED') return;
    await db.orders.update(order.id, { orderStatus: 'VOIDED', updatedAt: now });
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'ORDER_VOIDED',
      entityType: 'ORDER',
      entityId: order.id,
      actorId: input.actorId ?? 'cashier-local',
      deviceId: input.deviceId ?? 'device-local',
      metadata: JSON.stringify({ reason }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'ORDER_VOIDED',
      aggregateType: 'ORDER',
      aggregateId: order.id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify({
        orderId: order.id,
        voidReason: reason,
        deviceId: input.deviceId ?? 'device-local',
      }),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
}
